/*
 * Subtle colour system. Colour always carries meaning:
 * - taxonomy colours (set by admins in Categories) for categories, influence areas and graph nodes
 * - one soft hue per entity kind, so mixed lists scan quickly
 * - semantic tones for status, actions and roles
 * Class strings are written out in full so Tailwind generates them.
 */

export type EntityKind = 'stakeholder' | 'organization' | 'wiki' | 'media' | 'assignment' | 'relationship' | 'user';

export interface Tone {
  /** Soft tile/pill: tinted background + readable text */
  soft: string;
  /** Text/icon colour on white */
  text: string;
  /** Solid dot or bar */
  solid: string;
  /** Hex for canvas drawing and inline styles */
  hex: string;
}

export const ENTITY_TONE: Record<EntityKind, Tone> = {
  stakeholder: { soft: 'bg-sky-50 text-sky-700', text: 'text-sky-600', solid: 'bg-sky-500', hex: '#0ea5e9' },
  organization: { soft: 'bg-teal-50 text-teal-700', text: 'text-teal-600', solid: 'bg-teal-500', hex: '#14b8a6' },
  wiki: { soft: 'bg-violet-50 text-violet-700', text: 'text-violet-600', solid: 'bg-violet-500', hex: '#8b5cf6' },
  media: { soft: 'bg-indigo-50 text-indigo-700', text: 'text-indigo-600', solid: 'bg-indigo-500', hex: '#6366f1' },
  assignment: { soft: 'bg-slate-100 text-slate-700', text: 'text-slate-500', solid: 'bg-slate-400', hex: '#94a3b8' },
  relationship: { soft: 'bg-orange-50 text-orange-700', text: 'text-orange-600', solid: 'bg-orange-500', hex: '#f97316' },
  user: { soft: 'bg-neutral-100 text-neutral-700', text: 'text-neutral-500', solid: 'bg-neutral-400', hex: '#a3a3a3' },
};

/** Normalises the many spellings used across the API ('wiki_page', 'media_asset', …) */
export function entityKindOf(value: string | undefined | null): EntityKind | undefined {
  switch ((value || '').toLowerCase()) {
    case 'stakeholder':
    case 'stakeholders':
    case 'person':
      return 'stakeholder';
    case 'organization':
    case 'organizations':
    case 'organisation':
    case 'org':
      return 'organization';
    case 'wiki':
    case 'wiki_page':
    case 'wikipage':
    case 'page':
      return 'wiki';
    case 'media':
    case 'media_asset':
    case 'mediaasset':
      return 'media';
    case 'assignment':
    case 'role':
      return 'assignment';
    case 'relationship':
      return 'relationship';
    case 'user':
      return 'user';
    default:
      return undefined;
  }
}

/** One colour per relationship kind, distinct from the node colours (people sky, organizations teal) */
export const RELATION_TONE: Record<'ss' | 'oo' | 'os', Tone> = {
  os: { soft: 'bg-amber-50 text-amber-700', text: 'text-amber-600', solid: 'bg-amber-500', hex: '#f59e0b' },
  oo: { soft: 'bg-indigo-50 text-indigo-700', text: 'text-indigo-600', solid: 'bg-indigo-500', hex: '#6366f1' },
  ss: { soft: 'bg-pink-50 text-pink-700', text: 'text-pink-600', solid: 'bg-pink-500', hex: '#ec4899' },
};

export type SemanticTone = 'neutral' | 'green' | 'red' | 'amber' | 'blue' | 'violet' | 'teal' | 'outline';

/** Workflow / publication status */
export function statusTone(status: string | undefined | null): SemanticTone {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'published':
    case 'active':
    case 'completed':
    case 'success':
      return 'green';
    case 'pending':
    case 'pending_review':
    case 'draft_pending':
      return 'amber';
    case 'in_review':
    case 'under_review':
    case 'review':
    case 'reviewing':
      return 'blue';
    case 'rejected':
    case 'failed':
    case 'error':
    case 'deleted':
      return 'red';
    default:
      return 'neutral';
  }
}

/** Audit actions: creation green, change blue, removal red */
export function actionTone(action: string | undefined | null): SemanticTone {
  const a = (action || '').toLowerCase();
  if (/(delete|remove|reject|archive|revoke)/.test(a)) return 'red';
  if (/(create|add|approve|restore|register)/.test(a)) return 'green';
  if (/(update|edit|merge|change|assign|move)/.test(a)) return 'blue';
  if (/(import|export|backup|upload|download)/.test(a)) return 'violet';
  return 'neutral';
}

/** User roles, strongest permissions most saturated */
export function roleTone(role: string | undefined | null): SemanticTone {
  switch ((role || '').toLowerCase()) {
    case 'super_admin':
      return 'violet';
    case 'admin':
      return 'blue';
    case 'top_owner':
      return 'teal';
    default:
      return 'neutral';
  }
}

export type MediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'document' | 'other';

export const MEDIA_TONE: Record<MediaKind, Tone> = {
  image: { soft: 'bg-sky-50 text-sky-700', text: 'text-sky-600', solid: 'bg-sky-500', hex: '#0ea5e9' },
  video: { soft: 'bg-violet-50 text-violet-700', text: 'text-violet-600', solid: 'bg-violet-500', hex: '#8b5cf6' },
  audio: { soft: 'bg-orange-50 text-orange-700', text: 'text-orange-600', solid: 'bg-orange-500', hex: '#f97316' },
  pdf: { soft: 'bg-rose-50 text-rose-700', text: 'text-rose-600', solid: 'bg-rose-500', hex: '#f43f5e' },
  document: { soft: 'bg-slate-100 text-slate-700', text: 'text-slate-500', solid: 'bg-slate-400', hex: '#64748b' },
  other: { soft: 'bg-neutral-100 text-neutral-600', text: 'text-neutral-500', solid: 'bg-neutral-400', hex: '#a3a3a3' },
};

/* ---------- Arbitrary colours (taxonomy colours are user-defined hex values) ---------- */

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(value: string | undefined | null): value is string {
  return !!value && HEX.test(value.trim());
}

function toRgb(hex: string): [number, number, number] {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `#3b82f6` + 0.1 → `rgba(59, 130, 246, 0.1)` */
export function hexAlpha(hex: string, alpha: number): string {
  if (!isHexColor(hex)) return `rgba(163, 163, 163, ${alpha})`;
  const [r, g, b] = toRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Mixes the colour towards black so light hues stay readable as text on white */
export function readableOn(hex: string, amount = 0.35): string {
  if (!isHexColor(hex)) return '#525252';
  const [r, g, b] = toRgb(hex).map((c) => Math.round(c * (1 - amount)));
  return `rgb(${r}, ${g}, ${b})`;
}
